import { localAccountKeyError } from '@streamlain/view-config/local-data-seal';

import type {
  LocalIndexBlockRef,
  LocalIndexPosting,
} from '../../../local-data/payload';
import type {
  IndexMetadata,
  IndexPosting,
  IndexReadTransaction,
  IndexRecord,
  IndexWriteTransaction,
  MetadataWriter,
  PostingKeyIndex,
  PostingNidIndex,
  PostingWriter,
  RecordIndex,
  RecordWriter,
  TableIndex,
} from './access';

/** Metadata changes of one batch over the published statistics. */
class Overlay<K, V> {
  readonly changes = new Map<K, V>();
  constructor(readonly base: Map<K, V>) {}
  get(key: K) {
    return this.changes.get(key) ?? this.base.get(key);
  }
  *entries(): IterableIterator<[K, V]> {
    for (const [key, value] of this.base)
      if (!this.changes.has(key)) yield [key, value];
    yield* this.changes;
  }
  publish() {
    for (const [key, value] of this.changes) this.base.set(key, value);
  }
}

type PostingKey = [table: string, key: ArrayBuffer];

/**
 * A posting as the RAM index keeps it: the key buffer is shared by every
 * posting of its key, and the positions are one flat number list
 * [i, l, n, start1, end1, ..., startN, endN, i, l, n, ...] instead of an
 * object and two arrays per position. Readers get the stored shape back.
 */
type MemoryPosting = {
  table: string;
  key: ArrayBuffer;
  nid: number;
  pos: number[] | null;
};

function compactPosting(posting: IndexPosting): MemoryPosting {
  let pos: number[] | null = null;
  if (posting.pos) {
    pos = [];
    for (const { i, l, rs } of posting.pos) {
      pos.push(i, l, rs.length);
      for (const [start, end] of rs) pos.push(start, end);
    }
  }
  return {
    table: posting.table,
    key: posting.key.slice(0),
    nid: posting.nid,
    pos,
  };
}

function expandPosting(posting: MemoryPosting): IndexPosting {
  const expanded: IndexPosting = {
    table: posting.table,
    key: posting.key.slice(0),
    nid: posting.nid,
  };
  const flat = posting.pos;
  if (flat) {
    const pos: NonNullable<IndexPosting['pos']> = [];
    for (let at = 0; at < flat.length; ) {
      const i = flat[at++];
      const l = flat[at++];
      const count = flat[at++];
      const rs: [number, number][] = [];
      for (let n = 0; n < count; n++, at += 2)
        rs.push([flat[at], flat[at + 1]]);
      pos.push({ i, l, rs });
    }
    expanded.pos = pos;
  }
  return expanded;
}

/**
 * One char per key byte after the table: compact, and plain string order is
 * the order of indexedDB.cmp on [table, key] (tables hold no U+0000), so the
 * key list sorts without calling indexedDB.cmp.
 */
function postingKeyName([table, key]: PostingKey) {
  return `${table}\u0000${String.fromCharCode(...new Uint8Array(key))}`;
}

/** Postings kept decrypted across reads, about 32 MiB. */
export const SEALED_BLOCK_CACHE_POSTINGS = 200_000;
/** Records kept decrypted across reads. */
export const SEALED_RECORD_CACHE_RECORDS = 1000;
/** Blocks one lookup step reads together. */
const BLOCK_READ_BATCH = 8;

/** Decrypts what the sealed index reads on demand, under the current head. */
export interface SealedIndexLoader {
  /** Records of live nids; every requested nid must be stored. */
  records(nids: readonly number[]): Promise<Map<number, IndexRecord>>;
  /** Postings of the blocks by segment id. */
  blocks(
    refs: readonly LocalIndexBlockRef[]
  ): Promise<Map<string, LocalIndexPosting[]>>;
  /** Stored nid of each [table, id]: route lookups, nothing decrypted. */
  nids(keys: readonly [string, string][]): Promise<(number | undefined)[]>;
}

/** Least recently used entries up to a weight budget. */
class Lru<K, V> {
  private readonly entries = new Map<K, { value: V; weight: number }>();
  private weight = 0;
  constructor(private readonly budget: number) {}
  get(key: K): V | undefined {
    const entry = this.entries.get(key);
    if (!entry) return undefined;
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.value;
  }
  set(key: K, value: V, weight = 1) {
    this.delete(key);
    this.entries.set(key, { value, weight });
    this.weight += weight;
    for (const [oldest, entry] of this.entries) {
      if (this.weight <= this.budget || this.entries.size <= 1) break;
      this.entries.delete(oldest);
      this.weight -= entry.weight;
    }
  }
  delete(key: K) {
    const entry = this.entries.get(key);
    if (!entry) return;
    this.entries.delete(key);
    this.weight -= entry.weight;
  }
}

interface NameRange {
  lower: string;
  lowerOpen: boolean;
  upper: string;
  upperOpen: boolean;
}
function nameOf(key: unknown): string {
  if (
    !Array.isArray(key) ||
    key.length !== 2 ||
    typeof key[0] !== 'string' ||
    !(key[1] instanceof ArrayBuffer || ArrayBuffer.isView(key[1]))
  )
    throw localAccountKeyError('CORRUPT');
  const bytes = ArrayBuffer.isView(key[1])
    ? key[1].buffer.slice(
        key[1].byteOffset,
        key[1].byteOffset + key[1].byteLength
      )
    : key[1];
  return postingKeyName([key[0], bytes as ArrayBuffer]);
}
/** Posting key ranges are always bounded on both sides by [table, key]. */
function nameRange(query: PostingKey | IDBKeyRange): NameRange {
  if (!(query instanceof IDBKeyRange)) {
    const name = nameOf(query);
    return { lower: name, lowerOpen: false, upper: name, upperOpen: false };
  }
  return {
    lower: nameOf(query.lower),
    lowerOpen: query.lowerOpen,
    upper: nameOf(query.upper),
    upperOpen: query.upperOpen,
  };
}
const inRange = (range: NameRange, name: string) =>
  (range.lowerOpen ? name > range.lower : name >= range.lower) &&
  (range.upperOpen ? name < range.upper : name <= range.upper);
/** Index of the first posting at or after the range start in a sorted block. */
function firstInRange(
  postings: readonly LocalIndexPosting[],
  range: NameRange
) {
  let low = 0;
  let high = postings.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    const name = postings[mid][1];
    if (range.lowerOpen ? name <= range.lower : name < range.lower)
      low = mid + 1;
    else high = mid;
  }
  return low;
}
const byNameThenId = (a: LocalIndexPosting, b: LocalIndexPosting) =>
  a[1] < b[1] ? -1 : a[1] > b[1] ? 1 : a[0] - b[0];

/** The [table, key] a posting key name stands for. */
function postingKeyOf(name: string): PostingKey {
  const split = name.indexOf('\u0000');
  const bytes = new Uint8Array(name.length - split - 1);
  for (let at = 0; at < bytes.length; at++)
    bytes[at] = name.charCodeAt(split + 1 + at);
  return [name.slice(0, split), bytes.buffer];
}
export function storedPosting(id: number, posting: IndexPosting) {
  const compact = compactPosting(posting);
  return [
    id,
    postingKeyName([compact.table, compact.key]),
    compact.nid,
    compact.pos,
  ] satisfies LocalIndexPosting;
}
function readPosting([, name, nid, pos]: LocalIndexPosting): IndexPosting {
  const [table, key] = postingKeyOf(name);
  return expandPosting({ table, key, nid, pos });
}

/**
 * The account search index read from sealed rows on demand: RAM holds the
 * live record nids per table, the full-text statistics, the run directory
 * and bounded caches, never every record or posting. Postings of removed
 * records stay in their runs until a merge drops them; every read skips
 * postings whose nid is not live (word parts, nid 0, are always live).
 */
export class SealedIndex {
  /** Table of each live record by nid. */
  readonly live = new Map<number, string>();
  readonly tableCounts = new Map<string, number>();
  readonly metadata = new Map<string, IndexMetadata>();
  /** Runs of postings blocks, oldest first. */
  runs: LocalIndexBlockRef[][] = [];
  nextNid = 1;
  nextPosting = 1;
  private readonly blockCache = new Lru<string, LocalIndexPosting[]>(
    SEALED_BLOCK_CACHE_POSTINGS
  );
  private readonly recordCache = new Lru<number, IndexRecord>(
    SEALED_RECORD_CACHE_RECORDS
  );
  constructor(readonly loader: SealedIndexLoader) {}

  setLive(live: Map<number, string>) {
    this.live.clear();
    this.tableCounts.clear();
    for (const [nid, table] of live) {
      this.live.set(nid, table);
      this.tableCounts.set(table, (this.tableCounts.get(table) ?? 0) + 1);
    }
  }
  readonly(): IndexReadTransaction {
    return new SealedIndexTransaction(this, new Map());
  }
  /** `known` holds the stored nid (or null) of [table, id] keys read ahead. */
  readwrite(known: Map<string, number | null>) {
    return new SealedIndexTransaction(this, known);
  }

  async records(nids: readonly number[]) {
    const found = new Map<number, IndexRecord>();
    const missing: number[] = [];
    for (const nid of new Set(nids)) {
      if (!this.live.has(nid)) continue;
      const cached = this.recordCache.get(nid);
      if (cached) found.set(nid, cached);
      else missing.push(nid);
    }
    if (missing.length) {
      const loaded = await this.loader.records(missing);
      for (const nid of missing) {
        const record = loaded.get(nid);
        if (!record) throw localAccountKeyError('CORRUPT');
        this.recordCache.set(nid, record);
        found.set(nid, record);
      }
    }
    return found;
  }
  private async blocks(refs: readonly LocalIndexBlockRef[]) {
    const found = new Map<string, LocalIndexPosting[]>();
    const missing: LocalIndexBlockRef[] = [];
    for (const ref of refs) {
      const cached = this.blockCache.get(ref.segmentId);
      if (cached) found.set(ref.segmentId, cached);
      else missing.push(ref);
    }
    if (missing.length) {
      const loaded = await this.loader.blocks(missing);
      for (const ref of missing) {
        const postings = loaded.get(ref.segmentId);
        if (!postings) throw localAccountKeyError('CORRUPT');
        this.blockCache.set(ref.segmentId, postings, postings.length);
        found.set(ref.segmentId, postings);
      }
    }
    return found;
  }
  /**
   * Live postings in the key range by key name, then posting id: like the
   * IDB 'key' index. Blocks are read in key order and the scan stops once
   * `count` postings precede every unread block.
   */
  async postings(
    query: PostingKey | IDBKeyRange,
    alive: (nid: number) => boolean,
    count: number,
    added: (range: NameRange) => LocalIndexPosting[]
  ): Promise<LocalIndexPosting[]> {
    const range = nameRange(query);
    const found = added(range).filter(posting => alive(posting[2]));
    const candidates = this.runs
      .flatMap(run =>
        run.filter(
          block => block.first <= range.upper && block.last >= range.lower
        )
      )
      .sort((a, b) => (a.first < b.first ? -1 : a.first > b.first ? 1 : 0));
    for (let at = 0; at < candidates.length; ) {
      if (found.length >= count) {
        found.sort(byNameThenId);
        found.length = count;
        if (found[count - 1][1] < candidates[at].first) break;
      }
      const batch = candidates.slice(at, at + BLOCK_READ_BATCH);
      at += batch.length;
      const loaded = await this.blocks(batch);
      for (const block of batch) {
        const postings = loaded.get(block.segmentId) ?? [];
        // Blocks are sorted by name: start at the range, stop past it.
        for (
          let at = firstInRange(postings, range);
          at < postings.length;
          at++
        ) {
          const posting = postings[at];
          if (!inRange(range, posting[1])) break;
          if (alive(posting[2])) found.push(posting);
        }
      }
    }
    found.sort(byNameThenId);
    return found.slice(0, count);
  }
  forget(nid: number) {
    this.recordCache.delete(nid);
  }
  remember(nid: number, record: IndexRecord) {
    this.recordCache.set(nid, record);
  }
}

const recordKeyOf = (table: string, id: string) => JSON.stringify([table, id]);

/** Changes of one write batch over the sealed index, published after commit. */
export class SealedIndexTransaction implements IndexWriteTransaction {
  /** Record changes by nid; null removes the record. */
  readonly records = new Map<number, IndexRecord | null>();
  /** Postings this batch put, by posting id. */
  readonly added = new Map<number, LocalIndexPosting>();
  /** Ids of the added postings by key name, for lookups during the batch. */
  private readonly addedByName = new Map<string, Set<number>>();
  readonly metadata: Overlay<string, IndexMetadata>;
  nextNid: number;
  nextPosting: number;
  constructor(
    readonly index: SealedIndex,
    private readonly known: Map<string, number | null>
  ) {
    this.metadata = new Overlay(index.metadata);
    this.nextNid = index.nextNid;
    this.nextPosting = index.nextPosting;
  }
  objectStore(name: 'indexerRecords'): RecordWriter;
  objectStore(name: 'invertedIndex'): PostingWriter;
  objectStore(name: 'indexerMetadata'): MetadataWriter;
  objectStore(
    name: 'indexerRecords' | 'invertedIndex' | 'indexerMetadata'
  ): RecordWriter | PostingWriter | MetadataWriter {
    if (name === 'indexerRecords') return this.recordWriter();
    if (name === 'invertedIndex') return this.postingWriter();
    return this.metadataWriter();
  }
  /** Word parts (nid 0) are always live; removed records are not. */
  readonly alive = (nid: number) =>
    nid === 0 ||
    (this.records.has(nid)
      ? this.records.get(nid) !== null
      : this.index.live.has(nid));
  private async read(nids: readonly number[]) {
    const stored = await this.index.records(
      nids.filter(nid => !this.records.has(nid))
    );
    return nids.map(nid => {
      const record = this.records.has(nid)
        ? this.records.get(nid)
        : stored.get(nid);
      return record ? structuredClone(record) : undefined;
    });
  }
  private async recordKey(table: string, id: string) {
    for (const [nid, record] of this.records)
      if (record?.table === table && record.id === id) return nid;
    const key = recordKeyOf(table, id);
    let nid = this.known.get(key);
    if (nid === undefined) {
      [nid] = await this.index.loader.nids([[table, id]]);
      this.known.set(key, nid ?? null);
    }
    return nid === null || nid === undefined || this.records.has(nid)
      ? undefined
      : nid;
  }
  private tableNids(table: string) {
    const nids: number[] = [];
    for (const [nid, liveTable] of this.index.live)
      if (liveTable === table && !this.records.has(nid)) nids.push(nid);
    for (const [nid, record] of this.records)
      if (record?.table === table) nids.push(nid);
    return nids.sort((a, b) => a - b);
  }
  private tableCount(table: string) {
    let count = this.index.tableCounts.get(table) ?? 0;
    for (const [nid, record] of this.records) {
      if (this.index.live.get(nid) === table) count--;
      if (record?.table === table) count++;
    }
    return count;
  }
  private recordWriter(): RecordWriter {
    const index = ((name: 'id' | 'table'): RecordIndex | TableIndex =>
      name === 'id'
        ? { getKey: ([table, id]) => this.recordKey(table, id) }
        : {
            getAllKeys: async table => this.tableNids(table),
            count: async table => this.tableCount(table),
          }) as RecordWriter['index'];
    return {
      get: async nid => (await this.read([nid]))[0],
      getMany: nids => this.read(nids),
      index,
      put: async record => {
        const nid = this.nextNid++;
        this.records.set(nid, structuredClone(record));
        return nid;
      },
      delete: async nid => {
        this.records.set(nid, null);
      },
      clear: async () => {
        throw localAccountKeyError('CORRUPT');
      },
    };
  }
  private postingWriter(): PostingWriter {
    const index = ((name: 'key' | 'nid'): PostingKeyIndex | PostingNidIndex =>
      name === 'key'
        ? {
            getAll: async (query, count) =>
              (
                await this.index.postings(
                  query,
                  this.alive,
                  count ?? Infinity,
                  this.addedIn
                )
              ).map(readPosting),
          }
        : // Postings of a removed record stay until a merge; reads skip them.
          { getAllKeys: async () => [] }) as PostingWriter['index'];
    return {
      index,
      put: async posting => {
        const id = this.nextPosting++;
        const stored = storedPosting(id, posting);
        this.added.set(id, stored);
        const ids = this.addedByName.get(stored[1]);
        if (ids) ids.add(id);
        else this.addedByName.set(stored[1], new Set([id]));
        return id;
      },
      delete: async id => {
        const stored = this.added.get(id);
        if (!stored) return;
        this.added.delete(id);
        const ids = this.addedByName.get(stored[1]);
        ids?.delete(id);
        if (ids?.size === 0) this.addedByName.delete(stored[1]);
      },
      clear: async () => {
        throw localAccountKeyError('CORRUPT');
      },
    };
  }
  private metadataWriter(): MetadataWriter {
    return {
      get: async key => {
        const record = this.metadata.get(key);
        return record ? structuredClone(record) : undefined;
      },
      put: async record => {
        this.metadata.changes.set(record.key, structuredClone(record));
        return record.key;
      },
      clear: async () => {
        throw localAccountKeyError('CORRUPT');
      },
    };
  }
  /** Live records after this batch. */
  recordCount() {
    let count = this.index.live.size;
    for (const [nid, record] of this.records) {
      if (this.index.live.has(nid)) count--;
      if (record) count++;
    }
    return count;
  }
  /** Added postings in the range; one key is a lookup, a range scans the names. */
  private readonly addedIn = (range: NameRange) => {
    const names =
      range.lower === range.upper && !range.lowerOpen && !range.upperOpen
        ? [range.lower]
        : [...this.addedByName.keys()].filter(name => inRange(range, name));
    const found: LocalIndexPosting[] = [];
    for (const name of names)
      for (const id of this.addedByName.get(name) ?? [])
        found.push(this.added.get(id) as LocalIndexPosting);
    return found;
  };
  /** Postings to store: those of records still live after this batch, sorted. */
  storedPostings() {
    return [...this.added.values()]
      .filter(posting => this.alive(posting[2]))
      .sort(byNameThenId);
  }
  publish() {
    if (
      !Number.isSafeInteger(this.nextNid) ||
      this.nextNid < 1 ||
      !Number.isSafeInteger(this.nextPosting) ||
      this.nextPosting < 1
    )
      throw localAccountKeyError('CORRUPT');
    const { live, tableCounts } = this.index;
    const shift = (table: string, by: number) =>
      tableCounts.set(table, (tableCounts.get(table) ?? 0) + by);
    for (const [nid, record] of this.records) {
      const old = live.get(nid);
      if (old !== undefined) {
        live.delete(nid);
        shift(old, -1);
        this.index.forget(nid);
      }
      if (record) {
        live.set(nid, record.table);
        shift(record.table, 1);
        this.index.remember(nid, record);
      }
    }
    this.metadata.publish();
    this.index.nextNid = this.nextNid;
    this.index.nextPosting = this.nextPosting;
  }
}
